import {useState} from 'react';
import {
    Alert,
    Button,
    Divider,
    Group,
    List,
    Select,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import {AiOutlineInfoCircle, AiOutlineWarning} from 'react-icons/ai';
import {PlatformDrawer} from '@flexinet/ui-components';
import {useQuery} from '@tanstack/react-query';

import * as PlaybooksService from '../../playbooks/services/PlaybooksService';
import {Playbook, PlaybookStatus} from '../../playbooks/types';
import {Application} from '../types';
import {slugify, useNameAvailability} from '../../devops/name-availability';
import {readRepoNameSuffix} from '../../devops/name-availability/utils/playbookRepoNameSuffix';
import {DerivedNamesPanel} from '../components/DerivedNamesPanel';

export type ProvisionSubmitValues = {
    playbookId: string,
    stage: string,
    subdomain: string,
    baseName: string,
    deploymentAccountId: string,
    // FNP-1568: the addressing facts, collected here rather than on the
    // Provisioning tab. Grouped because the API refuses a partial set.
    addressing: {
        hostedZoneId: string,
        hostedZoneName: string,
        certificateArn: string,
    },
};

type ProvisionFormDrawerProps = {
    opened: boolean,
    onClose: () => void,
    onConfirm: (values: ProvisionSubmitValues) => Promise<void> | void,
    provisioning?: boolean,
    record?: Application,
};

const STAGE_OPTIONS = [
    {value: 'dev', label: 'dev'},
    {value: 'test', label: 'test'},
    {value: 'prod', label: 'prod'},
];

// FNP-1500: provisioning, as a drawer, showing what it will build.
//
// ⚠️ THIS REPLACES A RAW MANTINE `Modal`. The compliance checklist mandates the
// chain AddRecordButton → FormDrawer → PlatformDrawer → RecordForm, and
// `FormDrawer` must render `PlatformDrawer`, never a raw Mantine `Drawer`.
// `ProvisionApplicationModal` was outside that chain entirely.
//
// ⚠️ THE FOUR STEPS ARE NAMED because the operator should know what they are
// agreeing to. Provisioning is two repositories and two pipelines, and each
// repository is SEEDED BEFORE its pipeline is created — that ordering is what
// makes the repository analyser honest. By the time a pipeline is provisioned
// there is real content to read, so `cdk.json` yields CDK_INFRASTRUCTURE and a
// frontend `package.json` yields STATIC_SITE. An archetype guessed from an
// empty repo would be a guess about a template the platform itself just wrote.
export const ProvisionFormDrawer = ({
                                        opened,
                                        onClose,
                                        onConfirm,
                                        provisioning = false,
                                        record,
                                    }: ProvisionFormDrawerProps) => {

    const [playbookId, setPlaybookId] = useState<string | null>(null);
    const [stage, setStage] = useState<string | null>(null);
    const [subdomain, setSubdomain] = useState<string | null>(null);
    const [baseName, setBaseName] = useState<string | null>(null);
    const [deploymentAccountId, setDeploymentAccountId] = useState<string | null>(null);

    // FNP-1568: the addressing facts, asked HERE rather than found on the
    // Provisioning tab. `null` means untouched, so the record's value shows
    // through; typing replaces it for this run only.
    const [hostedZoneId, setHostedZoneId] = useState<string | null>(null);
    const [hostedZoneName, setHostedZoneName] = useState<string | null>(null);
    const [certificateArn, setCertificateArn] = useState<string | null>(null);

    // ⚠️ SEEDED THROUGH `slugify`, NOT RAW. Since FNP-1498 the record's name is
    // a human-friendly display name — "Admin Console Localhost" — and pasting
    // that straight in produced a base name the rules reject on sight, so the
    // drawer opened already showing an error the operator did not cause. The
    // suggestion has to be a name they could actually provision.
    //
    // Still editable: a slug is a guess at intent, not a decision.
    const seededBaseName = baseName ?? slugify(record?.name) ?? '';

    // FNP-1231: an EMPTY subdomain claims the zone apex. That is a real
    // feature — flexinet-marketing-app deploys at the apex deliberately — but
    // an apex is almost always already owned, so an unintended one asks
    // CloudFront for a CNAME another distribution holds and is refused with a
    // 409, after both repos and both pipelines exist.
    //
    // ⚠️ AND IT IS A SINGLE DNS LABEL, so it is slugified for the same reason
    // and then stripped of dots — "Admin Console Localhost" is not a hostname.
    // `record.subdomain` is already a label and is taken as-is.
    const seededSubdomain = subdomain
        ?? record?.subdomain
        ?? slugify(record?.name)?.replace(/\./g, '-')
        ?? '';

    // FNP-1568: SEEDED FROM THE RECORD, so the common case is "confirm and go"
    // rather than retyping three identifiers the record already holds.
    //
    // The resolver prefers what the drawer sends and falls back to the record,
    // so seeding and sending the same value is a no-op — the operator simply
    // gets to SEE it, and to change it for this run, which is the whole point
    // of the ticket.
    const seededHostedZoneId = hostedZoneId ?? record?.hostedZoneId ?? '';
    const seededHostedZoneName = hostedZoneName ?? record?.hostedZoneName ?? '';
    const seededCertificateArn = certificateArn ?? record?.certificateArn ?? '';

    // ⚠️ ALL THREE OR NONE — the API refuses a partial set rather than merging
    // it with a lookup (`isPartiallySupplied`), because a merge would read the
    // zone from one account and the certificate from another: resolvable only
    // by luck and silently wrong when it resolves. Catching it here means the
    // operator sees it while the drawer is open, not three steps into a run.
    const addressingSupplied = [
        seededHostedZoneId,
        seededHostedZoneName,
        seededCertificateArn,
    ].filter((value) => value.trim() !== '').length;

    const addressingPartial = addressingSupplied > 0 && addressingSupplied < 3;

    // FNP-1557: THE SUFFIX COMES FROM THE PLAYBOOK, so the playbook has to be
    // read before any name can be derived.
    //
    // The list query returns no steps — it is deliberately flat — so the chosen
    // playbook is fetched whole. `fetchForExport` is the existing query that
    // returns `steps { taskStateName parameters }`; reusing it keeps one
    // GraphQL document for the playbook tree rather than adding a near-copy.
    const {data: selectedPlaybook, isLoading: loadingSuffix} = useQuery({
        queryKey: ['playbooks', 'repo-name-suffix', playbookId],
        queryFn: () => PlaybooksService.fetchForExport(playbookId as string),
        enabled: opened && Boolean(playbookId),
        staleTime: 5 * 60 * 1000,
    });

    const suffixReading = readRepoNameSuffix(selectedPlaybook);

    // ⚠️ NO `targetStages` YET, AND THAT IS A KNOWN LIMITATION, NOT AN
    // OVERSIGHT. A TargetStage carries {stage, accountId}, and the account is
    // exactly what FNP-1518 shows is still hardcoded in the prod playbook —
    // `pipelineDeploymentAccounts` is pinned to shared-services prod. Passing a
    // guessed account would check the stack and bucket names in the WRONG
    // account and report them free when they are not.
    //
    // Omitting it checks the account-INDEPENDENT names — both repositories and
    // both pipelines — which is where the collision the owner hit actually
    // lives. The per-stage stack and bucket join once FNP-1518 makes the
    // deployment account a real choice.
    const {
        result: availability,
        isFetching: checkingNames,
        isPending: namesPending,
        allFree,
    } = useNameAvailability(seededBaseName, {
        repoNameSuffix: suffixReading.suffix,
        // ⚠️ HELD OFF UNTIL THE PLAYBOOK IS READ. Omitting the suffix is not
        // neutral — it checks the unsuffixed names, which is precisely the
        // defect FNP-1557 fixes. Running the check before the playbook loads
        // would show that wrong answer briefly and then correct it, and an
        // operator who clicked in that window would act on it.
        enabled: opened
            && Boolean(stage)
            && Boolean(playbookId)
            && !loadingSuffix
            && suffixReading.consistent,
    });

    const {data, isLoading, isError} = useQuery({
        queryKey: ['playbooks', 'provision-options'],
        queryFn: () => PlaybooksService.fetchRecords({}),
        enabled: opened,
        staleTime: 5 * 60 * 1000,
    });

    const playbooks: Playbook[] = data?.items ?? [];

    // Only an ACTIVE playbook bound to a state machine can run. workflows-api
    // enforces both — this filter spares the operator a choice that would be
    // refused, and is NOT a second gate. Do not add validation here.
    const runnable = playbooks.filter(playbook =>
        playbook.status === PlaybookStatus.ACTIVE && !!playbook.stateMachineArn,
    );

    const options = runnable.map(playbook => ({
        value: playbook.id,
        label: playbook.displayName?.trim() || playbook.name?.trim() || playbook.id,
    }));

    const skipped = playbooks.length - runnable.length;

    // ⚠️ A COLLISION OR AN UNCHECKABLE NAME BLOCKS PROVISION. `allFree` is
    // false for an UNKNOWN exactly as for a TAKEN, because a listing that
    // failed permissions returns zero items and proves nothing.
    const namesReady = allFree && !namesPending;
    // FNP-1518: a supplied account must be well-formed. Empty is fine and means
    // "use the playbook's"; twelve digits is fine; anything else is a typo that
    // would otherwise synthesize cleanly and fail at the provisioned app's
    // CloudFormation deploy, which is far too late to learn about it.
    const accountIdValid = !deploymentAccountId
        || deploymentAccountId.trim() === ''
        || /^\d{12}$/.test(deploymentAccountId.trim());

    const ready = Boolean(playbookId && stage)
        && namesReady
        && accountIdValid
        // FNP-1557: a playbook whose steps disagree cannot be name-checked, and
        // `allFree` would be false anyway since no check ran. Named explicitly
        // so the reason is visible rather than incidental.
        && suffixReading.consistent
        // FNP-1568: a partial addressing set is refused by the API rather than
        // merged, so blocking here turns a mid-run failure into a visible one.
        && !addressingPartial
        && !provisioning;

    return (
        <PlatformDrawer
            opened={opened}
            onClose={onClose}
            title={'Provision infrastructure'}
            size={'xl'}
        >
            <Stack gap={'md'}>

                <Alert variant={'light'} color={'blue'} icon={<AiOutlineInfoCircle/>}>
                    <Text size={'sm'} fw={500} mb={'xs'}>This runs four steps</Text>
                    <List size={'sm'} type={'ordered'}>
                        <List.Item>Create the infrastructure repository and seed it</List.Item>
                        <List.Item>Create its pipeline</List.Item>
                        <List.Item>Create the application repository and seed it</List.Item>
                        <List.Item>Create its pipeline</List.Item>
                    </List>
                    <Text size={'xs'} c={'dimmed'} mt={'xs'}>
                        Each repository is seeded before its pipeline is built,
                        so the pipeline is chosen from what the repository
                        actually contains. It takes several minutes.
                    </Text>
                </Alert>

                {isError && (
                    <Alert variant={'light'} color={'red'} icon={<AiOutlineInfoCircle/>}>
                        The playbooks could not be read, so there is nothing to
                        choose from.
                    </Alert>
                )}

                <Select
                    label={'Playbook'}
                    description={'The workflow that will build it. Only ACTIVE playbooks bound to a state machine can run.'}
                    placeholder={isLoading ? 'Loading…' : 'Select a playbook'}
                    data={options}
                    value={playbookId}
                    onChange={setPlaybookId}
                    disabled={isLoading || provisioning}
                    withAsterisk
                    searchable
                />

                {skipped > 0 && (
                    <Text size={'xs'} c={'dimmed'}>
                        {skipped} playbook{skipped === 1 ? ' is' : 's are'} not
                        listed — a playbook must be ACTIVE and bound to a state
                        machine before it can run.
                    </Text>
                )}

                <Select
                    label={'Stage'}
                    description={'Which environment to provision into. Chosen deliberately — there is no default.'}
                    placeholder={'Select a stage'}
                    data={STAGE_OPTIONS}
                    value={stage}
                    onChange={setStage}
                    disabled={provisioning}
                    withAsterisk
                />

                {/*
                  * ⚠️ THE BASE NAME IS NOT THE APPLICATION NAME. Since FNP-1498
                  * the record's name is human-friendly; this is the stem every
                  * repository, pipeline, stack and bucket name is derived from.
                  * Keeping them separate is what lets an application be called
                  * "Flexi Testing" while its repos are `flexi-testing-*`.
                  */}
                <TextInput
                    label={'Resource base name'}
                    description={'Repositories, pipelines, stacks and the deployment bucket are named from this. At most 41 characters.'}
                    value={seededBaseName}
                    onChange={(e) => setBaseName(e.currentTarget.value)}
                    disabled={provisioning}
                    withAsterisk
                    data-testid={'provision-base-name'}
                />

                {/*
                  * FNP-1557: A DISAGREEING PLAYBOOK IS SHOWN, NOT RESOLVED.
                  * Six steps each carry their own `repoNameSuffix`. If they
                  * differ, the run would create a repository under one name and
                  * its pipeline under another, and no single set of names is the
                  * right thing to check — so the panel says so and Provision
                  * stays disabled rather than presenting a guess as a verdict.
                  */}
                {playbookId && !suffixReading.consistent && (
                    <Alert variant={'light'} color={'red'} icon={<AiOutlineWarning/>}
                           title={'This playbook names resources inconsistently'}>
                        <Text size={'sm'}>
                            Its steps disagree about the repository name suffix
                            ({suffixReading.found.map(v => v === '' ? '(none)' : v).join(', ')}),
                            so the names it would create cannot be determined.
                            Fix the playbook's step parameters before provisioning.
                        </Text>
                    </Alert>
                )}

                {stage && playbookId && suffixReading.consistent ? (
                    <DerivedNamesPanel
                        result={availability}
                        isFetching={checkingNames || namesPending || loadingSuffix}
                    />
                ) : suffixReading.consistent ? (
                    <Text size={'xs'} c={'dimmed'}>
                        Choose a playbook and a stage to see what will be created.
                    </Text>
                ) : null}

                <TextInput
                    label={'Subdomain'}
                    description={'The leftmost DNS label, e.g. "reports" for reports.testing.flexi.network.'}
                    placeholder={'Leave empty to deploy at the domain apex'}
                    value={seededSubdomain}
                    onChange={(e) => setSubdomain(e.currentTarget.value)}
                    disabled={provisioning}
                    data-testid={'provision-subdomain'}
                />

                {seededSubdomain.trim() === '' && (
                    <Alert
                        variant={'light'}
                        color={'orange'}
                        icon={<AiOutlineWarning/>}
                        title={'This will deploy at the domain apex'}
                    >
                        <Text size={'sm'}>
                            With no subdomain this application claims the parent
                            domain itself. If another application already serves
                            that domain, provisioning fails partway — after the
                            repositories and pipelines have been created — and
                            the stack rolls back.
                        </Text>
                    </Alert>
                )}

                {/*
                  * FNP-1568: THE ADDRESSING FACTS, HERE RATHER THAN ON A
                  * SECOND SCREEN.
                  *
                  * The owner opened this drawer mid-provision, found only
                  * Subdomain and Deployment account, and had no way to know
                  * the zone and certificate lived on the Provisioning tab:
                  * "Why do we have two separate places we are putting stuff?"
                  *
                  * The API has accepted these three since FNP-1489 and the
                  * drawer simply never sent them, so this is the UI catching
                  * up with a contract that already existed.
                  *
                  * ⚠️ SEEDED FROM THE RECORD, so an operator who has already
                  * filled the Provisioning tab sees those values here and
                  * confirms them. Editing changes THIS RUN only; the tab
                  * remains where the record itself is corrected.
                  */}
                <Divider
                    my={'xs'}
                    label={'Addressing'}
                    labelPosition={'left'}
                />

                <Text size={'xs'} c={'dimmed'}>
                    The DNS zone and certificate this application is served
                    from. Filled in from the application record where it has
                    them — change a value to use something else for this run
                    only.
                </Text>

                <TextInput
                    label={'Hosted zone id'}
                    description={'Route 53 zone holding the DNS record'}
                    placeholder={'Z05954072QHC7JI7Y5TXM'}
                    value={seededHostedZoneId}
                    onChange={(e) => setHostedZoneId(e.currentTarget.value)}
                    disabled={provisioning}
                    data-testid={'provision-hosted-zone-id'}
                />

                <TextInput
                    label={'Hosted zone name'}
                    description={'The zone\'s own apex, e.g. testing.flexi.network'}
                    placeholder={'testing.flexi.network'}
                    value={seededHostedZoneName}
                    onChange={(e) => setHostedZoneName(e.currentTarget.value)}
                    disabled={provisioning}
                    data-testid={'provision-hosted-zone-name'}
                />

                <TextInput
                    label={'Certificate ARN'}
                    description={'ACM certificate serving the hostname'}
                    placeholder={'arn:aws:acm:us-east-1:…:certificate/…'}
                    value={seededCertificateArn}
                    onChange={(e) => setCertificateArn(e.currentTarget.value)}
                    disabled={provisioning}
                    data-testid={'provision-certificate-arn'}
                />

                {/*
                  * ⚠️ REFUSED, NOT MERGED. The API treats a partial set as an
                  * error rather than filling the gaps from the domains-api
                  * lookup, because that would read the zone from one account
                  * and the certificate from another. Blocking here shows it
                  * while the drawer is open instead of failing mid-run.
                  */}
                {addressingPartial && (
                    <Alert
                        variant={'light'}
                        color={'red'}
                        icon={<AiOutlineWarning/>}
                        title={'Addressing must be complete or empty'}
                    >
                        <Text size={'sm'}>
                            Supply the hosted zone id, the hosted zone name and
                            the certificate ARN together, or leave all three
                            empty to let the platform look them up. A partial
                            set is refused rather than filled in, because the
                            missing half would be resolved against a different
                            account.
                        </Text>
                    </Alert>
                )}

                {/*
                  * FNP-1518: WHERE THE PIPELINE DEPLOYS, as distinct from where
                  * the application is addressed.
                  *
                  * ⚠️ THE SUBDOMAIN AND ZONE ABOVE DECIDE THE ADDRESS. This
                  * decides which account gets the bucket, the distribution and
                  * the stack. They were capped separately: FNP-1489 opened the
                  * addressing half, and the pipeline stayed pinned to whatever
                  * the playbook named.
                  *
                  * ⚠️ EMPTY MEANS THE PLAYBOOK DECIDES, which is the behaviour
                  * every existing application relies on. Leaving this blank is
                  * the correct default, not an omission.
                  */}
                <TextInput
                    label={'Deployment account'}
                    description={
                        'The 12-digit account the pipeline deploys into. '
                        + 'Leave empty to use the playbook\'s own account.'
                    }
                    placeholder={'Leave empty to use the playbook default'}
                    value={deploymentAccountId ?? ''}
                    onChange={(e) => setDeploymentAccountId(e.currentTarget.value)}
                    disabled={provisioning}
                    error={
                        deploymentAccountId && !/^\d{12}$/.test(deploymentAccountId.trim())
                            ? 'An AWS account id is exactly 12 digits.'
                            : undefined
                    }
                    data-testid={'provision-deployment-account'}
                />

                {/*
                  * ⚠️ A ZONE IN ONE ACCOUNT AND A STACK IN ANOTHER CANNOT
                  * DEPLOY. The skeleton writes its Route 53 record with the
                  * deploying stack's own credentials, so it fails at the app's
                  * CloudFormation deploy, after both repos and both pipelines
                  * already exist. Say so here, where it is still free to fix.
                  */}
                {deploymentAccountId && /^\d{12}$/.test(deploymentAccountId.trim()) && (
                    <Alert
                        variant={'light'}
                        color={'blue'}
                        icon={<AiOutlineInfoCircle/>}
                        title={'This overrides the playbook'}
                    >
                        <Text size={'sm'}>
                            The pipeline will deploy into {deploymentAccountId.trim()} instead
                            of the account the playbook names. The hosted zone and certificate
                            above must live in that same account, or the provisioned
                            application cannot write its DNS record and the stack rolls back.
                        </Text>
                    </Alert>
                )}

                <Group justify={'flex-end'} mt={'md'}>
                    <Button variant={'subtle'} onClick={onClose} disabled={provisioning}>
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {
                            if (!playbookId || !stage) {
                                return;
                            }
                            // ⚠️ AWAIT BEFORE CLOSING. The reference drawer
                            // calls handleSubmit and closes in the same tick,
                            // which the checklist names at §7: a server-side
                            // refusal destroys the operator's input and toasts
                            // into a drawer that is no longer on screen. Here
                            // a refusal leaves the drawer open with everything
                            // still typed.
                            await onConfirm({
                                playbookId,
                                stage,
                                subdomain: seededSubdomain.trim(),
                                baseName: seededBaseName.trim(),
                                deploymentAccountId: (deploymentAccountId ?? '').trim(),
                                addressing: {
                                    hostedZoneId: seededHostedZoneId.trim(),
                                    hostedZoneName: seededHostedZoneName.trim(),
                                    certificateArn: seededCertificateArn.trim(),
                                },
                            });
                            onClose();
                        }}
                        disabled={!ready}
                        loading={provisioning}
                    >
                        Provision
                    </Button>
                </Group>

            </Stack>
        </PlatformDrawer>
    );
};
